import { useState, useEffect } from 'react'
import { Plus, Search, Pencil, Trash2, X, Package, ChevronRight, Clock, User } from 'lucide-react'
import api from '../../../core/services/api'
import { Toast, ConfirmModal } from '../../../components/Toast'
import {
  Articolo, DistintaBase, DistintaBaseDetail, Componente, Lavorazione,
  DistintaBaseForm, ComponenteForm, LavorazioneForm, PersonaleOption,
  TIPI_ARTICOLO_LABELS, TipoArticolo
} from '../../../types/magazzino'

const emptyDistintaForm: DistintaBaseForm = {
  articolo_id: '',
  codice: '',
  descrizione: '',
  note: '',
}

const emptyCompForm: ComponenteForm = {
  articolo_componente_id: '',
  quantita: '',
  note: '',
  ordine: 0,
}

const emptyLavForm: LavorazioneForm = {
  descrizione: '',
  ore_previste: '',
  personale_id: '',
  costo_orario: '0',
  note: '',
  ordine: 0,
}

export default function DistintaBasePage() {
  const [distinte, setDistinte] = useState<DistintaBase[]>([])
  const [articoli, setArticoli] = useState<Articolo[]>([])
  const [personale, setPersonale] = useState<PersonaleOption[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  // Dettaglio distinta selezionata
  const [selectedDistinta, setSelectedDistinta] = useState<DistintaBaseDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)

  // Modal crea distinta
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [distintaForm, setDistintaForm] = useState<DistintaBaseForm>(emptyDistintaForm)
  const [searchArticoloPadre, setSearchArticoloPadre] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Modal aggiungi componente
  const [showCompModal, setShowCompModal] = useState(false)
  const [editingComp, setEditingComp] = useState<Componente | null>(null)
  const [compForm, setCompForm] = useState<ComponenteForm>(emptyCompForm)
  const [searchCompArticolo, setSearchCompArticolo] = useState('')
  const [savingComp, setSavingComp] = useState(false)
  const [compError, setCompError] = useState('')

  // Modal aggiungi lavorazione
  const [showLavModal, setShowLavModal] = useState(false)
  const [editingLav, setEditingLav] = useState<Lavorazione | null>(null)
  const [lavForm, setLavForm] = useState<LavorazioneForm>(emptyLavForm)
  const [savingLav, setSavingLav] = useState(false)
  const [lavError, setLavError] = useState('')

  // Tab: componenti vs lavorazioni
  const [activeTab, setActiveTab] = useState<'componenti' | 'lavorazioni'>('componenti')

  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<{ type: 'distinta' | 'componente' | 'lavorazione'; id: string; label: string } | null>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [distRes, artRes, persRes] = await Promise.all([
        api.get('/v1/magazzino/distinte'),
        api.get('/v1/magazzino/articoli'),
        api.get('/v1/personale'),
      ])
      setDistinte(distRes.data)
      setArticoli(artRes.data)
      setPersonale(persRes.data.filter((p: any) => p.stato === 'attivo').map((p: any) => ({
        id: p.id,
        nome: p.nome,
        cognome: p.cognome,
        ruolo: p.ruolo,
      })))
    } catch {
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadDistintaDetail = async (id: string) => {
    try {
      setLoadingDetail(true)
      const res = await api.get(`/v1/magazzino/distinte/${id}`)
      setSelectedDistinta(res.data)
    } catch {
      setToast({ message: 'Errore nel caricamento dettaglio', type: 'error' })
    } finally {
      setLoadingDetail(false)
    }
  }

  const filteredDistinte = distinte.filter(d =>
    !search ||
    d.codice.toLowerCase().includes(search.toLowerCase()) ||
    d.articolo_codice.toLowerCase().includes(search.toLowerCase()) ||
    d.articolo_descrizione.toLowerCase().includes(search.toLowerCase())
  )

  // Articoli disponibili per BOM (solo semilavorato/prodotto_finito senza distinta)
  const articoliPerDistinta = articoli.filter(a =>
    a.attivo && (a.tipo === 'semilavorato' || a.tipo === 'prodotto_finito') && !a.has_distinta
  ).filter(a =>
    !searchArticoloPadre ||
    a.codice.toLowerCase().includes(searchArticoloPadre.toLowerCase()) ||
    a.descrizione.toLowerCase().includes(searchArticoloPadre.toLowerCase())
  )

  // Articoli disponibili come componenti (tutti, eccetto il padre)
  const articoliPerComponente = articoli.filter(a =>
    a.attivo && a.id !== selectedDistinta?.articolo_id
  ).filter(a =>
    !searchCompArticolo ||
    a.codice.toLowerCase().includes(searchCompArticolo.toLowerCase()) ||
    a.descrizione.toLowerCase().includes(searchCompArticolo.toLowerCase())
  )

  // --- CRUD Distinta ---
  const handleCreateDistinta = () => {
    setDistintaForm(emptyDistintaForm)
    setSearchArticoloPadre('')
    setError('')
    setShowCreateModal(true)
  }

  const handleSubmitDistinta = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!distintaForm.articolo_id) {
      setError('Seleziona un articolo padre')
      return
    }
    setError('')
    setSaving(true)
    try {
      const data = {
        articolo_id: distintaForm.articolo_id,
        codice: distintaForm.codice.trim(),
        descrizione: distintaForm.descrizione.trim() || null,
        note: distintaForm.note.trim() || null,
      }
      const res = await api.post('/v1/magazzino/distinte', data)
      setToast({ message: 'Distinta base creata', type: 'success' })
      setShowCreateModal(false)
      loadData()
      setSelectedDistinta(res.data)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Errore nella creazione')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteDistinta = async () => {
    if (!confirmDelete || confirmDelete.type !== 'distinta') return
    try {
      await api.delete(`/v1/magazzino/distinte/${confirmDelete.id}`)
      setToast({ message: 'Distinta eliminata', type: 'success' })
      setSelectedDistinta(null)
      loadData()
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore', type: 'error' })
    } finally {
      setConfirmDelete(null)
    }
  }

  // --- CRUD Componente ---
  const handleAddComponente = () => {
    setEditingComp(null)
    setCompForm(emptyCompForm)
    setSearchCompArticolo('')
    setCompError('')
    setShowCompModal(true)
  }

  const handleEditComponente = (comp: Componente) => {
    setEditingComp(comp)
    setCompForm({
      articolo_componente_id: comp.articolo_componente_id,
      quantita: String(comp.quantita),
      note: comp.note || '',
      ordine: comp.ordine,
    })
    setSearchCompArticolo('')
    setCompError('')
    setShowCompModal(true)
  }

  const handleSubmitComponente = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedDistinta) return
    if (!editingComp && !compForm.articolo_componente_id) {
      setCompError('Seleziona un articolo componente')
      return
    }
    setCompError('')
    setSavingComp(true)
    try {
      if (editingComp) {
        await api.put(`/v1/magazzino/distinte/${selectedDistinta.id}/componenti/${editingComp.id}`, {
          quantita: parseFloat(compForm.quantita),
          note: compForm.note.trim() || null,
          ordine: compForm.ordine,
        })
        setToast({ message: 'Componente aggiornato', type: 'success' })
      } else {
        await api.post(`/v1/magazzino/distinte/${selectedDistinta.id}/componenti`, {
          articolo_componente_id: compForm.articolo_componente_id,
          quantita: parseFloat(compForm.quantita),
          note: compForm.note.trim() || null,
          ordine: compForm.ordine,
        })
        setToast({ message: 'Componente aggiunto', type: 'success' })
      }
      setShowCompModal(false)
      loadDistintaDetail(selectedDistinta.id)
      loadData()
    } catch (err: any) {
      setCompError(err.response?.data?.detail || 'Errore')
    } finally {
      setSavingComp(false)
    }
  }

  const handleDeleteComponente = async () => {
    if (!confirmDelete || confirmDelete.type !== 'componente' || !selectedDistinta) return
    try {
      await api.delete(`/v1/magazzino/distinte/${selectedDistinta.id}/componenti/${confirmDelete.id}`)
      setToast({ message: 'Componente rimosso', type: 'success' })
      loadDistintaDetail(selectedDistinta.id)
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore', type: 'error' })
    } finally {
      setConfirmDelete(null)
    }
  }

  // --- CRUD Lavorazione ---
  const handleAddLavorazione = () => {
    setEditingLav(null)
    setLavForm(emptyLavForm)
    setLavError('')
    setShowLavModal(true)
  }

  const handleEditLavorazione = (lav: Lavorazione) => {
    setEditingLav(lav)
    setLavForm({
      descrizione: lav.descrizione,
      ore_previste: String(lav.ore_previste),
      personale_id: lav.personale_id || '',
      costo_orario: String(lav.costo_orario || 0),
      note: lav.note || '',
      ordine: lav.ordine,
    })
    setLavError('')
    setShowLavModal(true)
  }

  const handleSubmitLavorazione = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedDistinta) return
    setLavError('')
    setSavingLav(true)
    try {
      const data = {
        descrizione: lavForm.descrizione.trim(),
        ore_previste: parseFloat(lavForm.ore_previste),
        personale_id: lavForm.personale_id || null,
        costo_orario: parseFloat(lavForm.costo_orario) || 0,
        note: lavForm.note.trim() || null,
        ordine: lavForm.ordine,
      }
      if (editingLav) {
        await api.put(`/v1/magazzino/distinte/${selectedDistinta.id}/lavorazioni/${editingLav.id}`, data)
        setToast({ message: 'Lavorazione aggiornata', type: 'success' })
      } else {
        await api.post(`/v1/magazzino/distinte/${selectedDistinta.id}/lavorazioni`, data)
        setToast({ message: 'Lavorazione aggiunta', type: 'success' })
      }
      setShowLavModal(false)
      loadDistintaDetail(selectedDistinta.id)
    } catch (err: any) {
      setLavError(err.response?.data?.detail || 'Errore')
    } finally {
      setSavingLav(false)
    }
  }

  const handleDeleteLavorazione = async () => {
    if (!confirmDelete || confirmDelete.type !== 'lavorazione' || !selectedDistinta) return
    try {
      await api.delete(`/v1/magazzino/distinte/${selectedDistinta.id}/lavorazioni/${confirmDelete.id}`)
      setToast({ message: 'Lavorazione rimossa', type: 'success' })
      loadDistintaDetail(selectedDistinta.id)
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore', type: 'error' })
    } finally {
      setConfirmDelete(null)
    }
  }

  // Calcola totale ore e costo lavorazioni
  const totaleLavorazioni = selectedDistinta?.lavorazioni?.reduce((acc, l) => ({
    ore: acc.ore + (l.ore_previste || 0),
    costo: acc.costo + ((l.ore_previste || 0) * (l.costo_orario || 0)),
  }), { ore: 0, costo: 0 }) || { ore: 0, costo: 0 }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Distinta Base</h1>
          <p className="text-gray-500">Gestione BOM — componenti e lavorazioni per prodotti finiti e semilavorati</p>
        </div>
        <button
          onClick={handleCreateDistinta}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <Plus className="w-5 h-5" />
          Nuova Distinta
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista distinte */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm">
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca distinta..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>
            </div>
            <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
              {loading ? (
                <div className="p-4 text-center text-gray-500 text-sm">Caricamento...</div>
              ) : filteredDistinte.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">Nessuna distinta base</div>
              ) : (
                filteredDistinte.map(d => (
                  <button
                    key={d.id}
                    onClick={() => loadDistintaDetail(d.id)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      selectedDistinta?.id === d.id ? 'bg-primary-50 border-l-4 border-l-primary-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-mono text-sm font-medium text-gray-900">{d.codice}</div>
                        <div className="text-xs text-gray-500">{d.articolo_codice} — {d.articolo_descrizione}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">{d.componenti_count} comp.</span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Dettaglio distinta */}
        <div className="lg:col-span-2">
          {loadingDetail ? (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">Caricamento...</div>
          ) : !selectedDistinta ? (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">
              <Package className="w-12 h-12 mx-auto mb-3" />
              <p>Seleziona una distinta base dalla lista</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm">
              {/* Header distinta */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-gray-900">{selectedDistinta.codice}</h2>
                  <p className="text-sm text-gray-500">
                    {selectedDistinta.articolo_codice} — {selectedDistinta.articolo_descrizione}
                  </p>
                  {selectedDistinta.descrizione && (
                    <p className="text-sm text-gray-600 mt-1">{selectedDistinta.descrizione}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setConfirmDelete({ type: 'distinta', id: selectedDistinta.id, label: selectedDistinta.codice })}
                    className="p-2 text-gray-400 hover:text-red-600"
                    title="Elimina distinta"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Tabs: Componenti / Lavorazioni */}
              <div className="border-b border-gray-200 flex">
                <button
                  onClick={() => setActiveTab('componenti')}
                  className={`flex-1 px-4 py-3 text-sm font-medium text-center transition-colors ${
                    activeTab === 'componenti'
                      ? 'border-b-2 border-primary-500 text-primary-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Componenti ({selectedDistinta.componenti?.length || 0})
                </button>
                <button
                  onClick={() => setActiveTab('lavorazioni')}
                  className={`flex-1 px-4 py-3 text-sm font-medium text-center transition-colors ${
                    activeTab === 'lavorazioni'
                      ? 'border-b-2 border-primary-500 text-primary-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Lavorazioni ({selectedDistinta.lavorazioni?.length || 0})
                </button>
              </div>

              {/* TAB: Componenti */}
              {activeTab === 'componenti' && (
                <>
                  <div className="p-3 border-b border-gray-100 flex justify-end">
                    <button
                      onClick={handleAddComponente}
                      className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      Componente
                    </button>
                  </div>
                  {selectedDistinta.componenti.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">
                      Nessun componente. Clicca "Componente" per aggiungerne.
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Codice</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrizione</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qtà</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">UM</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Giacenza</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedDistinta.componenti.map((c, i) => (
                          <tr key={c.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-400">{i + 1}</td>
                            <td className="px-4 py-3 font-mono text-sm font-medium text-gray-900">{c.articolo_componente_codice}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{c.articolo_componente_descrizione}</td>
                            <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">{c.quantita}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">{c.articolo_componente_um}</td>
                            <td className={`px-4 py-3 text-sm text-right font-medium ${
                              (c.giacenza_componente || 0) <= 0 ? 'text-red-600' : 'text-green-600'
                            }`}>
                              {c.giacenza_componente}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 max-w-[120px] truncate">{c.note || '-'}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => handleEditComponente(c)} className="p-1 text-gray-400 hover:text-primary-600">
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => setConfirmDelete({ type: 'componente', id: c.id, label: c.articolo_componente_codice })}
                                  className="p-1 text-gray-400 hover:text-red-600"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}

              {/* TAB: Lavorazioni */}
              {activeTab === 'lavorazioni' && (
                <>
                  <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                    {(selectedDistinta.lavorazioni?.length || 0) > 0 && (
                      <div className="text-sm text-gray-500">
                        Totale: <span className="font-medium text-gray-900">{totaleLavorazioni.ore}h</span>
                        {totaleLavorazioni.costo > 0 && (
                          <> — Costo: <span className="font-medium text-gray-900">{totaleLavorazioni.costo.toFixed(2)} €</span></>
                        )}
                      </div>
                    )}
                    <button
                      onClick={handleAddLavorazione}
                      className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm ml-auto"
                    >
                      <Plus className="w-4 h-4" />
                      Lavorazione
                    </button>
                  </div>
                  {(!selectedDistinta.lavorazioni || selectedDistinta.lavorazioni.length === 0) ? (
                    <div className="p-8 text-center text-gray-400 text-sm">
                      Nessuna lavorazione. Clicca "Lavorazione" per aggiungere ore di assemblaggio.
                    </div>
                  ) : (
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descrizione</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Ore</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Personale</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">€/h</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Costo</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Azioni</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedDistinta.lavorazioni.map((l, i) => (
                          <tr key={l.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-400">{i + 1}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{l.descrizione}</td>
                            <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                              <span className="inline-flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5 text-gray-400" />
                                {l.ore_previste}h
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {l.personale_nome ? (
                                <span className="inline-flex items-center gap-1">
                                  <User className="w-3.5 h-3.5 text-gray-400" />
                                  {l.personale_nome}
                                </span>
                              ) : (
                                <span className="text-gray-400">Chiunque</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-500">{l.costo_orario}</td>
                            <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">
                              {((l.ore_previste || 0) * (l.costo_orario || 0)).toFixed(2)} €
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 max-w-[100px] truncate">{l.note || '-'}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={() => handleEditLavorazione(l)} className="p-1 text-gray-400 hover:text-primary-600">
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => setConfirmDelete({ type: 'lavorazione', id: l.id, label: l.descrizione })}
                                  className="p-1 text-gray-400 hover:text-red-600"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal Crea Distinta */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Nuova Distinta Base</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitDistinta} className="p-6 space-y-4">
              {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Articolo Padre (semilavorato/prodotto finito) *</label>
                <input
                  type="text"
                  placeholder="Cerca articolo..."
                  value={searchArticoloPadre}
                  onChange={(e) => setSearchArticoloPadre(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
                {distintaForm.articolo_id && (
                  <div className="mt-1 text-sm text-primary-600 font-medium">
                    {articoli.find(a => a.id === distintaForm.articolo_id)?.codice} — {articoli.find(a => a.id === distintaForm.articolo_id)?.descrizione}
                  </div>
                )}
                <div className="mt-1 max-h-32 overflow-y-auto border border-gray-100 rounded-lg">
                  {articoliPerDistinta.slice(0, 15).map(a => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => {
                        setDistintaForm({ ...distintaForm, articolo_id: a.id, codice: distintaForm.codice || `DB-${a.codice}` })
                        setSearchArticoloPadre('')
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 ${
                        distintaForm.articolo_id === a.id ? 'bg-primary-50 text-primary-700' : ''
                      }`}
                    >
                      <span className="font-mono font-medium">{a.codice}</span> — {a.descrizione}
                      <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                        a.tipo === 'semilavorato' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {TIPI_ARTICOLO_LABELS[a.tipo as TipoArticolo]}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Codice Distinta *</label>
                <input
                  type="text"
                  required
                  value={distintaForm.codice}
                  onChange={(e) => setDistintaForm({ ...distintaForm, codice: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="DB-001"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione</label>
                <input
                  type="text"
                  value={distintaForm.descrizione}
                  onChange={(e) => setDistintaForm({ ...distintaForm, descrizione: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Annulla</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                  {saving ? 'Creazione...' : 'Crea Distinta'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Aggiungi/Modifica Componente */}
      {showCompModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingComp ? 'Modifica Componente' : 'Aggiungi Componente'}
              </h2>
              <button onClick={() => setShowCompModal(false)} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitComponente} className="p-6 space-y-4">
              {compError && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{compError}</div>}

              {!editingComp && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Articolo Componente *</label>
                  <input
                    type="text"
                    placeholder="Cerca articolo..."
                    value={searchCompArticolo}
                    onChange={(e) => setSearchCompArticolo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                  />
                  {compForm.articolo_componente_id && (
                    <div className="mt-1 text-sm text-primary-600 font-medium">
                      {articoli.find(a => a.id === compForm.articolo_componente_id)?.codice} — {articoli.find(a => a.id === compForm.articolo_componente_id)?.descrizione}
                    </div>
                  )}
                  <div className="mt-1 max-h-32 overflow-y-auto border border-gray-100 rounded-lg">
                    {articoliPerComponente.slice(0, 20).map(a => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => { setCompForm({ ...compForm, articolo_componente_id: a.id }); setSearchCompArticolo('') }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 ${
                          compForm.articolo_componente_id === a.id ? 'bg-primary-50 text-primary-700' : ''
                        }`}
                      >
                        <span className="font-mono font-medium">{a.codice}</span> — {a.descrizione}
                        <span className="text-gray-400 ml-2">(giac: {a.giacenza_attuale} {a.unita_misura})</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantita' per unita' *</label>
                  <input
                    type="number"
                    step="0.0001"
                    min="0.0001"
                    required
                    value={compForm.quantita}
                    onChange={(e) => setCompForm({ ...compForm, quantita: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ordine</label>
                  <input
                    type="number"
                    value={compForm.ordine}
                    onChange={(e) => setCompForm({ ...compForm, ordine: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <input
                  type="text"
                  value={compForm.note}
                  onChange={(e) => setCompForm({ ...compForm, note: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowCompModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Annulla</button>
                <button type="submit" disabled={savingComp} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                  {savingComp ? 'Salvataggio...' : editingComp ? 'Aggiorna' : 'Aggiungi'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Aggiungi/Modifica Lavorazione */}
      {showLavModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingLav ? 'Modifica Lavorazione' : 'Aggiungi Lavorazione'}
              </h2>
              <button onClick={() => setShowLavModal(false)} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitLavorazione} className="p-6 space-y-4">
              {lavError && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{lavError}</div>}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descrizione lavorazione *</label>
                <input
                  type="text"
                  required
                  value={lavForm.descrizione}
                  onChange={(e) => setLavForm({ ...lavForm, descrizione: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Es. Assemblaggio telaio, Saldatura, Collaudo..."
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ore previste *</label>
                  <input
                    type="number"
                    step="0.25"
                    min="0.25"
                    required
                    value={lavForm.ore_previste}
                    onChange={(e) => setLavForm({ ...lavForm, ore_previste: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Costo orario (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={lavForm.costo_orario}
                    onChange={(e) => setLavForm({ ...lavForm, costo_orario: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ordine</label>
                  <input
                    type="number"
                    value={lavForm.ordine}
                    onChange={(e) => setLavForm({ ...lavForm, ordine: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Personale assegnato</label>
                <select
                  value={lavForm.personale_id}
                  onChange={(e) => setLavForm({ ...lavForm, personale_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">— Chiunque —</option>
                  {personale.map(p => (
                    <option key={p.id} value={p.id}>{p.cognome} {p.nome}{p.ruolo ? ` (${p.ruolo})` : ''}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <input
                  type="text"
                  value={lavForm.note}
                  onChange={(e) => setLavForm({ ...lavForm, note: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowLavModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Annulla</button>
                <button type="submit" disabled={savingLav} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
                  {savingLav ? 'Salvataggio...' : editingLav ? 'Aggiorna' : 'Aggiungi'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Confirm Delete */}
      <ConfirmModal
        isOpen={!!confirmDelete}
        title={
          confirmDelete?.type === 'distinta' ? 'Elimina Distinta' :
          confirmDelete?.type === 'lavorazione' ? 'Rimuovi Lavorazione' :
          'Rimuovi Componente'
        }
        message={`Sei sicuro di voler ${
          confirmDelete?.type === 'distinta' ? 'eliminare la distinta' :
          confirmDelete?.type === 'lavorazione' ? 'rimuovere la lavorazione' :
          'rimuovere il componente'
        } "${confirmDelete?.label}"?`}
        confirmText={confirmDelete?.type === 'distinta' ? 'Elimina' : 'Rimuovi'}
        onConfirm={
          confirmDelete?.type === 'distinta' ? handleDeleteDistinta :
          confirmDelete?.type === 'lavorazione' ? handleDeleteLavorazione :
          handleDeleteComponente
        }
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  )
}
